<template>
  <div class="payouts-page">
    <router-link class="breadcrumb" :to="{ name: routes.admin.adminDashboard }">
      <span class="breadcrumb-icon">&lt;</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>
    <div class="page-header">
      <div>
        <p class="eyebrow">{{ $adminT('adminAll.settlements_ee59e6', 'SETTLEMENTS') }}</p>
        <h1>{{ $adminT('adminAll.ticket_seller_payouts_d5fcd9', 'Ticket Seller Payouts') }}</h1>
        <p class="lede">
          {{ $adminT('adminAll.generate_calculation_summary_and_compensation_pdfs_for_ext_bd2653', 'Generate calculation, summary, and compensation PDFs for external ticket sellers (e.g. Tisak, Dirty Old Shop).') }}
        </p>
      </div>
      <div class="summary">
        <span class="summary-count">{{ ticketSellers.length || '—' }}</span>
        <span class="summary-label">{{ $adminT('adminAll.sellers_a4f23d', 'SELLERS') }}</span>
      </div>
    </div>

    <div class="card">
      <div class="form-grid">
        <div class="field event-field">
          <label>{{ $adminT('adminAll.event_id_optional_6cd72b', 'Event ID (optional)') }}</label>
          <input
            v-model.number="form.eventId"
            type="text"
            :placeholder="$adminT('adminAll.leave_blank_to_include_all_events_1307ea', 'Leave blank to include all events')"
          >
          <small class="hint">
            {{ $adminT('adminAll.if_empty_payouts_are_generated_across_all_events_with_sale_492869', 'If empty, payouts are generated across all events with sales in the period.') }}
          </small>
        </div>
        <div class="field sellers-field">
          <label>{{ $adminT('adminAll.ticket_seller_required_8c0920', 'Ticket seller (required)') }}</label>
          <div class="seller-search">
            <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M14 14L10.5 10.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <input
              v-model="sellerSearch"
              type="text"
              :placeholder="$adminT('adminAll.search_sellers_2dedc0', 'Search sellers...')"
              class="search-input"
            >
          </div>
          <div class="seller-list">
            <div v-if="sellersLoading" class="seller-list-skeleton" aria-hidden="true">
              <span v-for="index in 5" :key="`seller-skeleton-${index}`" class="seller-list-skeleton__row" />
            </div>
            <label v-for="seller in filteredSellers" :key="seller.id" class="seller-row">
              <input v-model="selectedSellerId" type="radio" name="ticketSeller" :value="seller.id">
              <div>
                {{ seller.name }} <span class="seller-id">{{ $adminT('adminAll.id_4c660d', '(ID:') }} {{ seller.id }})</span>
              </div>
            </label>
            <p v-if="!filteredSellers.length && !sellersLoading && sellerSearch" class="hint">
              {{ $adminT('adminAll.no_sellers_match_your_search_9d485f', 'No sellers match your search.') }}
            </p>
            <p v-if="!ticketSellers.length && !sellersLoading && !sellerSearch" class="hint">
              {{ $adminT('adminAll.no_external_ticket_sellers_found_1b042c', 'No external ticket sellers found.') }}
            </p>
            <div v-if="sellersError" class="alert error">{{ adminAllMessage(sellersError) }}</div>
          </div>
          <small class="hint">
            {{ $adminT('adminAll.select_exactly_one_external_ticket_seller_organization_id__528f0f', 'Select exactly one external ticket seller (organization_id is null).') }}
          </small>
        </div>
        <div class="field date-field">
          <label>{{ $adminT('adminAll.from_date_inclusive_c0b3cc', 'From date (inclusive)') }}</label>
          <input v-model="form.from" type="date" :max="form.to || undefined">
        </div>
        <div class="field date-field">
          <label>{{ $adminT('adminAll.to_date_inclusive_f52264', 'To date (inclusive)') }}</label>
          <input v-model="form.to" type="date" :min="form.from || undefined">
        </div>
      </div>

      <div class="actions">
        <main-button class="" flat :loading="loading" @click.native="downloadPayout">
          {{ $adminT('adminAll.download_payout_zip_1c4d57', 'Download payout ZIP') }}
        </main-button>
        <main-button class="" flat @click.native="resetForm">{{ $adminT('adminAll.reset_44c57a', 'Reset') }}</main-button>
      </div>

      <div v-if="selectedSeller" class="seller-settings">
        <div class="seller-settings-header">
          <div>
            <h2>{{ $adminT('adminAll.seller_fee_settings_65f274', 'Seller fee settings') }}</h2>
            <p>{{ $adminT('adminAll.buyer_fee_and_kickback_are_stored_on_the_selected_ticket_s_9ae36f', 'Buyer fee and kickback are stored on the selected ticket seller organization.') }}</p>
          </div>
          <main-button flat :loading="savingSettings" @click.native="saveSellerSettings">
            {{ $adminT('adminAll.save_settings_913aba', 'Save settings') }}
          </main-button>
        </div>
        <div class="settings-grid">
          <div class="field">
            <label>{{ $adminT('adminAll.buyer_fee_per_ticket_eur_04b746', 'Buyer fee per ticket (EUR)') }}</label>
            <input
              v-model.number="sellerSettings.ticketAdditionalFeeAmount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
            >
          </div>
          <div class="field">
            <label>{{ $adminT('adminAll.kickback_per_ticket_eur_725c4a', 'Kickback per ticket (EUR)') }}</label>
            <input
              v-model.number="sellerSettings.kickbackPerTicket"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
            >
          </div>
        </div>
      </div>

      <div v-if="error" class="alert error">{{ adminAllMessage(error) }}</div>
      <div v-if="success" class="alert success">{{ adminAllMessage(success) }}</div>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import {
  downloadTicketSellerPayoutDocuments,
  getTicketSellerOrganizations,
  updateTicketSellerOrganizationFees,
} from '@/http'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'ticket-seller-payouts-admin',
  data() {
    return {
      loading: false,
      error: null,
      success: null,
      form: {
        eventId: null,
        from: '',
        to: '',
      },
      ticketSellers: [],
      selectedSellerId: null,
      sellersLoading: false,
      sellersError: null,
      sellerSearch: '',
      savingSettings: false,
      sellerSettings: {
        ticketAdditionalFeeAmount: 0,
        kickbackPerTicket: 0,
      },
    }
  },
  computed: {
    selectedSeller() {
      return this.ticketSellers.find(seller => Number(seller.id) === Number(this.selectedSellerId)) || null
    },
    filteredSellers() {
      if (!this.sellerSearch.trim()) {
        return this.ticketSellers
      }
      const search = this.sellerSearch.toLowerCase()

      return this.ticketSellers.filter(
        seller => seller.name.toLowerCase().includes(search) || String(seller.id).includes(search),
      )
    },
  },
  watch: {
    selectedSeller: {
      immediate: true,
      handler(seller) {
        this.sellerSettings = this.extractSellerSettings(seller)
      },
    },
  },
  created() {
    this.loadTicketSellers()
  },
  methods: {
    extractSellerSettings(seller) {
      const fees = (seller && seller.fees) || {}
      const ticketAdditionalFeeAmount =
        seller && seller.ticketAdditionalFeeAmount != null
          ? seller.ticketAdditionalFeeAmount
          : fees.ticketAdditionalFeeAmount
      const kickbackPerTicket =
        seller && seller.kickbackPerTicket != null ? seller.kickbackPerTicket : fees.kickbackPerTicket

      return {
        ticketAdditionalFeeAmount: Number(ticketAdditionalFeeAmount || 0),
        kickbackPerTicket: Number(kickbackPerTicket || 0),
      }
    },
    async normalizeAxiosError(err) {
      const fallback =
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        this.adminAllNotice('Failed to generate payout documents.')

      const data = err && err.response && err.response.data
      if (!data) return fallback

      const parseText = async text => {
        if (!text) return fallback
        try {
          const parsed = JSON.parse(text)

          return (parsed && parsed.message) || text || fallback
        } catch (_) {
          return text || fallback
        }
      }

      try {
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
          return await parseText(await data.text())
        }
        if (typeof data === 'string') {
          return await parseText(data)
        }
        if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
          const decoded = new TextDecoder('utf-8').decode(new Uint8Array(data))

          return await parseText(decoded)
        }
      } catch (_) {
        // ignore
      }

      return fallback
    },
    async loadTicketSellers() {
      this.sellersLoading = true
      this.sellersError = null
      try {
        const { data } = await getTicketSellerOrganizations()
        this.ticketSellers = (data && data.ticketSellerOrganizations) || []
      } catch (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load ticket sellers.')
        this.sellersError = message
      } finally {
        this.sellersLoading = false
      }
    },
    resetForm() {
      this.form = {
        eventId: null,
        from: '',
        to: '',
      }
      this.error = null
      this.success = null
      this.selectedSellerId = null
      this.sellerSettings = {
        ticketAdditionalFeeAmount: 0,
        kickbackPerTicket: 0,
      }
    },
    async saveSellerSettings() {
      if (!this.selectedSellerId) {
        this.error = this.adminAllNotice('Select a ticket seller before saving settings.')

        return
      }

      this.error = null
      this.success = null
      this.savingSettings = true
      try {
        const payload = {
          ticketAdditionalFeeAmount: Number(this.sellerSettings.ticketAdditionalFeeAmount || 0),
          kickbackPerTicket: Number(this.sellerSettings.kickbackPerTicket || 0),
        }
        const { data } = await updateTicketSellerOrganizationFees(this.selectedSellerId, payload)
        const updatedSeller = (data && data.ticketSellerOrganization) || {
          ...this.selectedSeller,
          ...payload,
        }
        this.ticketSellers = this.ticketSellers.map(seller =>
          Number(seller.id) === Number(this.selectedSellerId)
            ? { ...seller, ...updatedSeller }
            : seller,
        )
        this.sellerSettings = this.extractSellerSettings(updatedSeller)
        this.success = this.adminAllNotice('Ticket seller fee settings saved.')
      } catch (err) {
        this.error = await this.normalizeAxiosError(err)
      } finally {
        this.savingSettings = false
      }
    },
    async downloadPayout() {
      this.error = null
      this.success = null
      const eventId = this.form.eventId ? Number(this.form.eventId) : undefined
      if (!this.form.from || !this.form.to) {
        this.error = this.adminAllNotice('Select both "From" and "To" dates.')

        return
      }
      if (this.form.from && this.form.to && this.form.from > this.form.to) {
        this.error = this.adminAllNotice('"From" date cannot be after "To" date.')

        return
      }
      if (!this.selectedSellerId) {
        this.error = this.adminAllNotice('Select a ticket seller.')

        return
      }

      this.loading = true
      try {
        const response = await downloadTicketSellerPayoutDocuments(eventId || undefined, {
          from: this.form.from || undefined,
          to: this.form.to || undefined,
          ticketSellerOrganizationIds: [this.selectedSellerId],
        })

        const blob = new Blob([response.data], { type: 'application/zip' })
        const disposition = response.headers['content-disposition']
        let filename = 'ticket-seller-payouts.zip'
        if (disposition) {
          const match = disposition.match(/filename=\"?([^\";]+)\"?/)
          if (match && match[1]) {
            filename = match[1]
          }
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.success = this.adminAllNotice('Payout ZIP downloaded.')
      } catch (err) {
        this.error = await this.normalizeAxiosError(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.payouts-page {
  padding: 28px;
  min-height: 100vh;
  color: #111827;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 1280px;
  margin: 0 auto;
}
.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #2563eb;
  font-weight: 500;
  padding: 0;
  margin-bottom: 12px;
  cursor: pointer;
  text-decoration: none;
  font-size: 14px;
}
.breadcrumb-icon {
  font-size: 14px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 20px;
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  font-weight: 500;
}
h1 {
  margin: 4px 0;
  font-size: 30px;
  color: #0f172a;
  font-weight: 700;
}
.lede {
  margin: 6px 0 0;
  color: #4b5563;
  max-width: 580px;
  font-size: 15px;
  line-height: 1.5;
}
.summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  min-width: 110px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  text-transform: uppercase;
  font-size: 11px;
  color: #6b7280;
}
.summary-count {
  font-weight: 800;
  font-size: 26px;
  color: #0f172a;
  line-height: 1.1;
}
.summary-label {
  margin-top: 2px;
}
.card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  align-items: start;
}
.field label {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
  color: #111827;
}
.field input[type='text'],
.field input[type='number'],
.field input[type='date'] {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
}
.field input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.field input::placeholder {
  color: #9ca3af;
}
.event-field {
  display: flex;
  flex-direction: column;
}
.sellers-field {
  display: flex;
  flex-direction: column;
}
.seller-search {
  position: relative;
  margin-bottom: 8px;
}
.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}
.search-input {
  width: 100%;
  padding: 10px 12px 10px 36px !important;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
}
.search-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.search-input::placeholder {
  color: #9ca3af;
}
.seller-list {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  background: #fff;
  margin-bottom: 8px;
}
.seller-row {
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;
  padding: 8px 0;
  cursor: pointer;
  font-size: 14px;

  color: #111827;
}
.seller-row:hover {
  background: #f8fafc;
}
.seller-row input[type='radio'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2563eb;
}
.seller-id {
  color: #9ca3af;
}
.seller-list-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.seller-list-skeleton__row {
  display: block;
  width: 100%;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: seller-skeleton-shimmer 1.4s ease-in-out infinite;
}
.hint {
  color: #6b7280;
  display: block;
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.4;
}
.date-field {
  display: flex;
  flex-direction: column;
}
.actions {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.seller-settings {
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
}
.seller-settings-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 16px;
}
.seller-settings-header h2 {
  margin: 0 0 4px;
  font-size: 18px;
}
.seller-settings-header p {
  margin: 0;
  color: #4b5563;
  font-size: 14px;
}
.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.alert {
  padding: 10px 12px;
  border-radius: 10px;
  margin-top: 16px;
  border: 1px solid transparent;
}
.alert.error {
  background: #fef2f2 !important;
  color: #b91c1c;
  border-color: #fecdd3;
}
.alert.success {
  background: #ecfdf3;
  color: #15803d;
  border-color: #bbf7d0;
}
@keyframes seller-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
@media (max-width: 1024px) {
  .payouts-page {
    padding: 20px;
  }
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 640px) {
  .payouts-page {
    padding: 14px;
  }
  .card {
    padding: 18px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .settings-grid {
    grid-template-columns: 1fr;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .summary {
    align-self: flex-start;
  }
  .actions,
  .seller-settings-header {
    flex-direction: column;
    align-items: stretch;
  }
  .seller-row {
    gap: 0.75rem;
  }
}
</style>
